import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import { COUNTRIES } from '../../constants/countries';
import { SignupSchema } from '../../validators/signupSchema';
import '../../styles/Auth.css';

function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const inviteFromURL = new URLSearchParams(location.search).get('invite');

  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    email: '',
    password: '',
    sex: '',
    date_of_birth: '',
    profile_url: '',
    country_code: '',
    agree: false,
  });
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  // Country combobox state
  const [countryQuery, setCountryQuery] = useState('');
  const [showCountryList, setShowCountryList] = useState(false);
  const [countryHighlightIndex, setCountryHighlightIndex] = useState(-1);

  useEffect(() => {
    // Style tweak for sex select placeholder
    const selectElement = document.querySelector(".auth-select[name='sex']");
    if (selectElement && selectElement.value === '') {
      selectElement.classList.add('placeholder-shown');
    }
    // Sync country label if preselected
    if (formData.country_code) {
      const current = COUNTRIES.find((c) => c.code === formData.country_code);
      if (current) setCountryQuery(current.name);
    }
  }, []);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear field-level error on change
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleFocus = (e) => {
    e.target.classList.remove('placeholder-shown');
  };

  const handleBlur = (e) => {
    if (e.target.value === '') {
      e.target.classList.add('placeholder-shown');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Resolve country code if user typed exact name but didn't click
    const selectedByCode = COUNTRIES.find((c) => c.code === formData.country_code);
    let resolvedCountryCode = selectedByCode?.code;
    if (!resolvedCountryCode && countryQuery.trim()) {
      const exactByName = COUNTRIES.find((c) => c.name.toLowerCase() === countryQuery.trim().toLowerCase());
      if (exactByName) {
        resolvedCountryCode = exactByName.code;
        setFormData((prev) => ({ ...prev, country_code: exactByName.code }));
      }
    }

    // Build candidate object for strict validation
    const candidate = {
      full_name: formData.full_name,
      username: formData.username,
      email: formData.email,
      password: formData.password,
      sex: formData.sex,
      date_of_birth: formData.date_of_birth,
      country_code: resolvedCountryCode ?? '',
      agree: formData.agree,
      ...(inviteFromURL ? { invite_token: inviteFromURL } : {}),
    };

    try {
      SignupSchema.parse(candidate);
      setErrors({});
    } catch (err) {
      if (err && err.errors) {
        const fieldErrors = {};
        for (const issue of err.errors) {
          const field = issue.path?.[0];
          if (field && !fieldErrors[field]) fieldErrors[field] = issue.message;
        }
        setErrors(fieldErrors);
        setError('Please fix the highlighted fields');
        return;
      }
      // Fallback unknown error
      setError('Validation failed. Please check your inputs.');
      return;
    }
    try {
      // Only send fields the backend expects
      const { full_name, username, email, password, sex, date_of_birth, profile_url, agree } = formData;
      const payload = { full_name, username, email, password, sex, date_of_birth, profile_url, country_code: resolvedCountryCode, agree };
      if (inviteFromURL) payload.invite_token = inviteFromURL;

      await axiosInstance.post('/auth/signup', payload);
      navigate('/home');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Signup failed');
    }
  };

  const handleDateChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, date_of_birth: value }));
    setErrors((prev) => ({ ...prev, date_of_birth: undefined }));
  };

  // Country searchable combobox
  const filteredCountries = COUNTRIES.filter((c) => c.name.toLowerCase().includes(countryQuery.trim().toLowerCase()));

  const handleCountryInputChange = (e) => {
    const q = e.target.value;
    setCountryQuery(q);
    setShowCountryList(true);
    setCountryHighlightIndex(-1);
    // Clear selected code until user selects again
    setFormData((prev) => ({ ...prev, country_code: '' }));
    setErrors((prev) => ({ ...prev, country_code: undefined }));
  };

  const selectCountry = (country) => {
    setFormData((prev) => ({ ...prev, country_code: country.code }));
    setCountryQuery(country.name);
    setShowCountryList(false);
    setCountryHighlightIndex(-1);
    setErrors((prev) => ({ ...prev, country_code: undefined }));
  };

  const handleCountryKeyDown = (e) => {
    if (!showCountryList) return;
    const max = filteredCountries.length - 1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCountryHighlightIndex((idx) => (idx < max ? idx + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCountryHighlightIndex((idx) => (idx > 0 ? idx - 1 : max));
    } else if (e.key === 'Enter') {
      if (countryHighlightIndex >= 0 && countryHighlightIndex <= max) {
        e.preventDefault();
        selectCountry(filteredCountries[countryHighlightIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowCountryList(false);
    }
  };

  const handleCountryBlur = () => {
    setTimeout(() => setShowCountryList(false), 120);
  };

  return (
    <div className="auth-container">
      <h1 style={{ marginTop: '32px', marginBottom: '5px' }}>trippin</h1>
      <div className="auth-card" style={{ paddingTop: 0 }}>
        <form onSubmit={handleSubmit}>
          <input
            className="auth-input"
            name="full_name"
            onChange={handleChange}
            placeholder="Full Name"
            aria-invalid={Boolean(errors.full_name)}
          />
          {errors.full_name && (
            <span style={{ color: '#FF3040', fontSize: 13, marginTop: 6, display: 'block' }}>{errors.full_name}</span>
          )}

          <input
            className="auth-input"
            name="username"
            onChange={handleChange}
            placeholder="Username"
            aria-invalid={Boolean(errors.username)}
          />
          {errors.username && (
            <span style={{ color: '#FF3040', fontSize: 13, marginTop: 6, display: 'block' }}>{errors.username}</span>
          )}

          <input
            className="auth-input"
            name="email"
            onChange={handleChange}
            placeholder="Email"
            type="email"
            aria-invalid={Boolean(errors.email)}
          />
          {errors.email && (
            <span style={{ color: '#FF3040', fontSize: 13, marginTop: 6, display: 'block' }}>{errors.email}</span>
          )}

          <input
            className="auth-input"
            name="password"
            onChange={handleChange}
            type="password"
            placeholder="Password"
            aria-invalid={Boolean(errors.password)}
          />
          {errors.password && (
            <span style={{ color: '#FF3040', fontSize: 13, marginTop: 6, display: 'block' }}>{errors.password}</span>
          )}

          <select
            className="auth-select"
            name="sex"
            value={formData.sex}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            aria-invalid={Boolean(errors.sex)}
          >
            <option value="" hidden>
              -- Select Gender --
            </option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
          {errors.sex && (
            <span style={{ color: '#FF3040', fontSize: 13, marginTop: 6, display: 'block' }}>{errors.sex}</span>
          )}

          {/* Country searchable combobox (local JSON) */}
          <div style={{ position: 'relative', padding: 0, display: 'flex', flexDirection: 'column' }} role="combobox" aria-expanded={showCountryList} aria-haspopup="listbox">
            <input
              type="text"
              className="auth-input"
              placeholder="Country"
              value={countryQuery}
              onChange={handleCountryInputChange}
              onFocus={() => setShowCountryList(true)}
              onBlur={handleCountryBlur}
              onKeyDown={handleCountryKeyDown}
              autoComplete="off"
              name="country_search"
              aria-invalid={Boolean(errors.country_code)}
            />

            {showCountryList && (
              <ul
                role="listbox"
                className="absolute top-full left-0 right-0 max-h-56 overflow-y-auto bg-white dark:bg-darkBackground z-20 list-none m-0 p-0 shadow rounded-lg"
              >
                {(filteredCountries.length ? filteredCountries : COUNTRIES)
                  .slice(0, 200)
                  .map((c) => {
                    return (
                      <li
                        key={c.code}
                        role="option"
                        aria-selected={formData.country_code === c.code}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectCountry(c)}
                        className="py-2.5 px-3 cursor-pointer border-t border-gray-100 dark:border-neutral-800 text-black dark:text-white first:border-t-0"
                      >
                        {c.name}
                      </li>
                    );
                  })}
              </ul>
            )}
            {errors.country_code && (
              <span style={{ color: '#FF3040', fontSize: 13, marginTop: 6, display: 'block' }}>{errors.country_code}</span>
            )}
          </div>

          <div className="date-input-container">
            <input
              type="date"
              className="auth-input"
              name="date_of_birth"
              onChange={handleDateChange}
              aria-invalid={Boolean(errors.date_of_birth)}
            />
            {errors.date_of_birth && (
              <span style={{ color: '#FF3040', fontSize: 13, marginTop: 6, display: 'block' }}>{errors.date_of_birth}</span>
            )}
          </div>
          <div style={{ margin: '8px 0' }}>
            <label style={{ fontSize: '16px', accentColor: '#48c9b0' }}>
              <input
                type="checkbox"
                name="agree"
                checked={formData.agree}
                onChange={handleChange}
                style={{ marginRight: '8px' }}
                aria-invalid={Boolean(errors.agree)}
              />
              I agree to the&nbsp;
              <span style={{ color: '#48c9b0', cursor: 'pointer', textDecoration: 'underline', fontSize: '16px' }} onClick={() => navigate('/termsandconditions')}>
                Terms and Conditions
              </span>
            </label>
            {errors.agree && (
              <div>
                <span style={{ color: '#FF3040', fontSize: 13, marginTop: 6, display: 'block' }}>{errors.agree}</span>
              </div>
            )}
          </div>

          <button className="auth-button" type="submit">
            Sign Up
          </button>
        </form>
        <p style={{ fontSize: '17px' }}>
          already have an account? <Link to="/login" className="auth-link">Login</Link>
        </p>
        {error && <p className="auth-error">{error}</p>}
      </div>
    </div>
  );
}

export default Signup;
